import { useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import styles from "./DraftEnquiries.module.css";

const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  const d = new Date(dateStr);
  if (isNaN(d)) return "N/A";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatShortDate = (dateStr) => {
  if (!dateStr) return "N/A";
  const d = new Date(dateStr);
  if (isNaN(d)) return "N/A";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
};

const DraftRow = ({
  draft,
  onReject,
  onAccept,
  isSelected,
  isInvalid,
  onToggleSelect,
}) => {
  const history = useHistory();
  const rowRef = useRef(null);
  const laneDetails = draft?.enquiry_lane_details?.lane_details;

  useEffect(() => {
    if (isInvalid && rowRef.current) {
      rowRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [isInvalid]);
  const missing = draft?.missing_fields || [];
  const isComplete = missing.length === 0;
  const lspCount =
    draft?.enquiry_lane_details?.benchmark_transporter_list?.length || 0;
  const submissionDate = formatShortDate(draft?.created_at);

  const handleEditClick = (draftId) => {
    history.push(`/trucks/drafts/${draftId}`);
  };

  return (
    <div
      ref={rowRef}
      className={`${styles.draftItem} ${isSelected ? styles.draftItemSelected : ""} ${isInvalid ? styles.draftItemInvalid : ""}`}
    >
      {/* Checkbox column — full-height strip on the left */}
      <div
        className={styles.checkboxCol}
        onClick={() => onToggleSelect(draft.draft_id)}
      >
        <input
          type="checkbox"
          className={styles.checkbox}
          checked={isSelected}
          onChange={() => onToggleSelect(draft.draft_id)}
          onClick={(e) => e.stopPropagation()}
        />
      </div>

      {/* Existing card content */}
      <div className={styles.draftItemContent}>
        {/* Row 1: badge | meta */}
        <div className={styles.draftItemRow1}>
          <div className={styles.badgeWrap}>
            {!isComplete && (
              <span
                className={styles.badgeIncomplete}
                title={`Missing ${missing.length} required field(s) — click Edit to complete before accepting`}
              >
                <i className="fa fa-exclamation-circle"></i> Incomplete &middot;{" "}
                {missing.length}
              </span>
            )}
          </div>

          <div className={styles.draftItemMeta}>
            <span title="Date this freight request was received by the LoRRI Spot Agent">
              <i className="fa fa-calendar-o"></i> {submissionDate}
            </span>
            {lspCount > 0 && (
              <span
                title={`${lspCount} pre-shortlisted Logistics Service Provider(s) for this lane`}
              >
                <i className="fa fa-users"></i> {lspCount} LSP
                {lspCount > 1 ? "s" : ""}
              </span>
            )}
          </div>
        </div>

        <div className={styles.laneSummary}>
          <span className={styles.laneCity} title="Origin / Pickup location">
            {laneDetails?.origin?.location?.location || (
              <span className={styles.missingText}>Missing Origin</span>
            )}
          </span>
          <div className={styles.laneArrowWrap}>
            <i className={`fa fa-long-arrow-right ${styles.laneArrowIcon}`}></i>
          </div>
          <span
            className={styles.laneCity}
            title="Destination / Delivery location"
          >
            {laneDetails?.destination?.location?.location || (
              <span className={styles.missingText}>Missing Destination.</span>
            )}
          </span>
        </div>

        {/* Row 3: chips + actions */}
        <div className={styles.chipActionRow}>
          <div className={styles.chipRow}>
            {laneDetails?.truck_types?.label ? (
              <span
                className={styles.chip}
                title="Truck type required for this shipment"
              >
                <i className="fa fa-truck"></i> {laneDetails.truck_types.label}
              </span>
            ) : (
              <span
                className={`${styles.chip} ${styles.chipMissing}`}
                title="Truck type is required — click Edit to specify"
              >
                <i className="fa fa-exclamation-triangle"></i> No Truck Type
              </span>
            )}
            {laneDetails?.truck_required && (
              <span className={styles.chip} title="Number of trucks required">
                {laneDetails.truck_required} truck
                {laneDetails.truck_required > 1 ? "s" : ""}
              </span>
            )}
            {laneDetails?.contract_tenure?.[0]?.label && (
              <span className={styles.chip} title="Contract duration">
                {laneDetails.contract_tenure[0].label}
              </span>
            )}
            {laneDetails?.due_date && (
              <span
                className={styles.chipDue}
                title="Shipment deadline — trucks must be arranged by this date"
              >
                <i className="fa fa-clock-o"></i> Due{" "}
                {formatDate(laneDetails.due_date)}
              </span>
            )}
            {laneDetails?.tonnage && (
              <span
                className={styles.chip}
                title="Cargo weight in Metric Tonnes"
              >
                {laneDetails.tonnage} {laneDetails?.tonnage_unit || "MT"}
              </span>
            )}
          </div>

          <div className={styles.inlineActions}>
            <button
              className={styles.btnEdit}
              onClick={() => handleEditClick(draft.draft_id)}
              title="Edit draft details"
            >
              <i className="fa fa-pencil"></i> Edit
            </button>
            {isComplete && (
              <button
                className={styles.btnAcceptItem}
                onClick={() => onAccept(draft)}
                title="Accept and publish as a live spot enquiry"
              >
                <i className="fa fa-check"></i> Accept
              </button>
            )}
            <button
              className={styles.btnRejectItem}
              onClick={() => onReject(draft.draft_id)}
              title="Reject and permanently delete this draft"
            >
              <i className="fa fa-times"></i>
            </button>
          </div>
        </div>

        {/* AI reasoning (1-line clamp) */}
        {draft?.missing_fields?.length > 0 && draft.reasoning && (
          <p className={styles.reasoningSnippet}>
            <i
              className="fa fa-lightbulb-o"
              style={{ marginRight: "5px", color: "#f59e0b" }}
            ></i>
            {draft.reasoning}
          </p>
        )}

        {/* Additional note */}
        {laneDetails?.additional_details && (
          <p className={styles.additionalNote}>
            <i
              className="fa fa-sticky-note-o"
              style={{ marginRight: "5px" }}
            ></i>
            {laneDetails.additional_details}
          </p>
        )}
      </div>
    </div>
  );
};

export default DraftRow;
